import React, { useContext, useEffect, useState } from "react";
import { Loader2, ChevronLeft, Check, FileText } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { callComConfigApi } from "./callComConfigApi";
import AuthContext  from "../../../store/auth-context";
import ActiveStoreContext from "../../../store/ActiveStoreContext";
import styles from "./ComConfigTemplateSelection.module.css";

const ComConfigTemplateSelection = () => {
    const { eventId, channelId, recipientTypeId } = useParams();
    const languageCode = "en";
    const navigate = useNavigate();
    const authCtx = useContext(AuthContext);
    const { activeStore: activeStoreJson } = useContext(ActiveStoreContext);
    const activeStore = (() => { try { return activeStoreJson ? JSON.parse(activeStoreJson) : null; } catch { return null; } })();

    const [slot, setSlot] = useState(null);
    const [selectedVersionId, setSelectedVersionId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        loadSlot();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId, channelId, recipientTypeId, languageCode, activeStore?.storeIdentifier]);

    const loadSlot = async () => {
        if (!activeStore?.storeIdentifier || !eventId || !channelId || !recipientTypeId) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setMessage(null);

        const res = await callComConfigApi(
            "comConfigGetTemplateSlots",
            {
                storeIdentifier: activeStore.storeIdentifier,
                eventId,
                channelId,
                recipientTypeId,
                languageCode,
            },
            authCtx
        );

        if (res?.status === "success") {
            setSlot(res.response);
            setSelectedVersionId(res.response?.selectedTemplateVersionId || res.response?.versions?.[0]?.ID || null);
        } else {
            setMessage({ type: "error", text: res?.message || "No template configured for this combination yet." });
        }

        setIsLoading(false);
    };

    const handleSave = async () => {
        if (!slot?.ID || !selectedVersionId) return;

        setIsSaving(true);
        const res = await callComConfigApi(
            "comConfigSelectTemplateVersion",
            {
                templateId: slot.ID,
                templateVersionId: selectedVersionId,
                authIdentifier: authCtx?.userId,
            },
            authCtx
        );

        if (res?.status === "success") {
            setMessage({ type: "success", text: "Template selected." });
        } else {
            setMessage({ type: "error", text: res?.message || "Couldn't save. Try again." });
        }
        setIsSaving(false);
    };

    if (isLoading) {
        return (
            <div className={styles.stateContainer}>
                <Loader2 className={styles.spinner} size={22} />
                <span>Loading templates…</span>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <button className={styles.backLink} onClick={() => navigate(`/communication-settings/${eventId}`)}>
                <ChevronLeft size={16} />
                Back
            </button>

            <div className={styles.header}>
                <h1 className={styles.title}>Choose a message</h1>
                <p className={styles.subtitle}>
                    {slot?.storeIdentifier === "PLATFORM"
                        ? "Using Lokaci's pre-approved templates for this slot."
                        : "Using your own approved templates for this slot."}
                </p>
            </div>

            {message && message.type === "error" && !slot ? (
                <div className={styles.messageError}>{message.text}</div>
            ) : (
                <>
                    <div className={styles.versionList}>
                        {(slot?.versions || []).map((version) => {
                            const isSelected = version.ID === selectedVersionId;
                            return (
                                <button
                                    key={version.ID}
                                    className={isSelected ? styles.versionCardSelected : styles.versionCard}
                                    onClick={() => setSelectedVersionId(version.ID)}
                                >
                                    <div className={styles.versionCardHeader}>
                                        <FileText size={15} />
                                        <span className={styles.versionLabel}>Version {version.versionNo}</span>
                                        {isSelected && <Check size={16} className={styles.checkIcon} />}
                                    </div>
                                    <p className={styles.versionContent}>{version.content}</p>
                                    {version.externalTemplateRef && (
                                        <span className={styles.versionRef}>{version.externalTemplateRef}</span>
                                    )}
                                </button>
                            );
                        })}

                        {(!slot?.versions || slot.versions.length === 0) && (
                            <div className={styles.emptyState}>No approved content variants yet.</div>
                        )}
                    </div>

                    {message && message.type === "success" && (
                        <div className={styles.messageSuccess}>{message.text}</div>
                    )}
                    {message && message.type === "error" && slot && (
                        <div className={styles.messageError}>{message.text}</div>
                    )}

                    <div className={styles.actions}>
                        <button
                            className={styles.saveButton}
                            onClick={handleSave}
                            disabled={isSaving || !selectedVersionId}
                        >
                            {isSaving ? <Loader2 size={15} className={styles.spinnerInline} /> : null}
                            Use this message
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default ComConfigTemplateSelection;